import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Cell,
  LabelList,
} from "recharts";

const data = [
  { period: "Per month", lost: 505, saved: 1000 },
  { period: "Per year", lost: 6059, saved: 12000 },
  { period: "Over 3 years", lost: 18177, saved: 36000 },
];

const fmt = (v) => `£${v.toLocaleString("en-GB")}`;

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-lg p-4 text-sm">
      <div className="font-semibold text-[#0A2540] mb-2">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2 mb-1 last:mb-0">
          <span className="h-2 w-2 rounded-full" style={{ background: p.color }} />
          <span className="text-slate-600">{p.dataKey === "lost" ? "Funding & margin changes" : "Cascade Plus savings"}</span>
          <span className="font-semibold text-[#0A2540] ml-auto">{p.dataKey === "lost" ? `-${fmt(p.value)}` : fmt(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

function ValueLabel({ x, y, width, value, dataKey }) {
  const isLoss = dataKey === "lost";
  return (
    <text
      x={x + width / 2}
      y={y - 8}
      fill={isLoss ? "#EF4444" : "#10B981"}
      textAnchor="middle"
      className="text-xs font-bold"
    >
      {isLoss ? "-" : ""}
      {fmt(value)}
    </text>
  );
}

export default function FundingChart() {
  return (
    <div className="w-full h-[340px]" data-testid="funding-cuts-chart">
      <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={300} initialDimension={{ width: 500, height: 340 }}>
        <BarChart data={data} barGap={8} margin={{ top: 20, right: 10, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
          <XAxis dataKey="period" stroke="#64748B" fontSize={12} tickLine={false} axisLine={false} />
          <YAxis
            stroke="#64748B"
            fontSize={12}
            tickLine={false}
            axisLine={false}
            domain={[0, 40000]}
            ticks={[0, 5000, 10000, 15000, 20000, 25000, 30000, 35000, 40000]}
            tickFormatter={(v) => (v >= 1000 ? `£${v / 1000}k` : `£${v}`)}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(15, 23, 42, 0.03)" }} />
          <Bar dataKey="lost" radius={[8, 8, 0, 0]}>
            <LabelList content={(props) => <ValueLabel {...props} dataKey="lost" />} />
            {data.map((d) => (
              <Cell key={`lost-${d.period}`} fill="#EF4444" />
            ))}
          </Bar>
          <Bar dataKey="saved" radius={[8, 8, 0, 0]}>
            <LabelList content={(props) => <ValueLabel {...props} dataKey="saved" />} />
            {data.map((d) => (
              <Cell key={`saved-${d.period}`} fill="#10B981" />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <div className="flex items-center gap-6 justify-center mt-4 text-xs">
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-sm bg-rose-500" />
          <span className="text-slate-600">Funding & margin changes (loss)</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-sm bg-emerald-500" />
          <span className="text-slate-600">Cascade Plus savings</span>
        </div>
      </div>
    </div>
  );
}
